import { ChevronDown } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { CountBadge } from '../CountBadge';

export interface ProfileNavItem {
  key: string;
  label: string;
  icon?: ReactNode;
  /** Rendered as a badge on the right. `0` still shows — an explicit zero is information. */
  count?: number;
  /** Hides the entry entirely when the caller lacks it, matching the app sidebar. */
  permission?: string;
  /**
   * A thin RULE above this entry — it starts a new run of the rail (09 Oct 2026: the evidence rail
   * sets Requests apart from the folders). Decoration only: `aria-hidden`, not an entry.
   */
  dividerBefore?: boolean;
}

export interface ProfileNavGroup {
  key: string;
  label: string;
  icon?: ReactNode;
  items: ProfileNavItem[];
  /** Groups start open; a long one can start closed. */
  defaultOpen?: boolean;
}

export type ProfileNavEntry = ProfileNavItem | ProfileNavGroup;

function isGroup(entry: ProfileNavEntry): entry is ProfileNavGroup {
  return 'items' in entry;
}

/**
 * The panel picker in a `<ProfileShell>` sidebar.
 *
 * **The selected entry carries NO extra weight** — `bg-blue-50 text-slate-900`, at the body
 * weight. Product call, 15 Sep 2026. A rail is a column of short labels read top to bottom,
 * and one bolded row in it reads as a heading over the rows under it rather than as the row
 * you are on; a tab strip has nothing beneath a tab to head, which is why the weight stays
 * there.
 *
 * This brought the component into line rather than out of it: the app sidebar already marked its
 * selection with `text-slate-900` and no weight, and this was the rail disagreeing. Two of
 * the three signals are untouched — the entry is the darkest text here (slate-900 against
 * slate-600) and the only one on a tint.
 *
 * **The collapsed group header moves with it.** That header takes the selected treatment when
 * it is standing in for a hidden active leaf, so a weight on one and not the other would make
 * the same state look like two.
 *
 * Flat entries and collapsible groups, each leaf carrying an optional count. The count is
 * what makes the nav worth reading before clicking: a reader can see which panels hold
 * anything without opening them, so a zero is rendered rather than hidden.
 *
 * Selection is the caller's state — this component never mutates it, so the panel and the
 * nav cannot disagree about which one is showing.
 */
export function ProfileNav({
  entries,
  activeKey,
  onSelect,
  hasPermission,
  id,
}: {
  entries: ProfileNavEntry[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Supplied by the caller so this component takes no dependency on the auth context. */
  hasPermission?: (permission: string) => boolean;
  id: string;
}) {
  const permitted = (item: ProfileNavItem) =>
    !item.permission || !hasPermission || hasPermission(item.permission);

  return (
    <nav aria-label="Record sections" className="flex flex-col gap-0.5 p-2">
      {entries.map((entry) =>
        isGroup(entry) ? (
          <NavGroup
            key={entry.key}
            group={{ ...entry, items: entry.items.filter(permitted) }}
            activeKey={activeKey}
            onSelect={onSelect}
            id={id}
          />
        ) : permitted(entry) ? (
          <Fragment key={entry.key}>
            {entry.dividerBefore ? <hr aria-hidden="true" className="my-1.5 border-slate-200" /> : null}
            <NavLeaf item={entry} active={entry.key === activeKey} onSelect={onSelect} id={id} />
          </Fragment>
        ) : null,
      )}
    </nav>
  );
}

function NavGroup({
  group,
  activeKey,
  onSelect,
  id,
}: {
  group: ProfileNavGroup;
  activeKey: string;
  onSelect: (key: string) => void;
  id: string;
}) {
  const [open, setOpen] = useState(group.defaultOpen ?? true);

  const holdsActive = group.items.some((item) => item.key === activeKey);

  /*
    **A group holding the open panel still collapses.** It used to be forced open —
    `open || holdsActive` — on the reasoning that hiding the active entry loses the reader
    their place. That reasoning was written for a rail of several small groups, where you
    sit in one and collapse the others.

    It breaks the moment a profile puts most of its panels in ONE group: the reader is then
    almost always inside it, the chevron never does anything, and a control that does
    nothing is worse than a missing one. The personnel profile puts all but two of its
    entries under `201 File`.

    What replaces the guard is the header itself: collapsed over the active panel, it takes
    the selected treatment, so "where am I" is still answered by the one row still on
    screen. The panel content never went anywhere either.
  */
  const expanded = open;

  if (group.items.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        id={`${id}-group-${group.key}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
        /*
          Collapsed over the active panel, the header IS the selection — same treatment a
          selected leaf takes, because it is standing in for one. Expanded it is a plain
          header: the active leaf below it carries the selection itself, and two selected
          rows would be the same state in two slots.
        */
        className={[
          'flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition',
          !expanded && holdsActive
            ? 'bg-blue-50 text-slate-900'
            : 'text-slate-700 hover:bg-slate-50',
        ].join(' ')}
      >
        {group.icon ? (
          <span
            className={`shrink-0 ${!expanded && holdsActive ? 'text-slate-900' : 'text-slate-400'}`}
            aria-hidden="true"
          >
            {group.icon}
          </span>
        ) : null}

        <span className="min-w-0 flex-1 truncate">{group.label}</span>

        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${expanded ? '' : '-rotate-90'}`}
          aria-hidden="true"
        />
      </button>

      {/*
        A RULE DOWN THE WHOLE RUN OF CHILDREN, top to bottom.

        It is the border of the container that holds them, never a per-row decoration: a line
        drawn on each leaf breaks at every `gap-0.5` and reads as a column of ticks rather than
        as one rule saying "these belong to the group above".

        `ml-5` puts it under the CENTRE of the header's icon — `px-3` (12px) plus half of the
        16px icon — which is the arithmetic to keep in step if either changes. Left of that it
        cuts through the icon; right of it, it stops looking like it descends from the group.

        `slate-200` is the chrome tone this product gives a structural rule on white, the same
        one the org chart's lane rules take. It is not `slate-300`: that step is for the sidebar
        rail, whose tinted ground swallows slate-200 — this sits on the profile shell's white,
        where it reads.

        It is drawn only while the group is EXPANDED, because it is the children's own left
        edge: a rule under a collapsed group would descend from the header into nothing.
      */}
      {expanded ? (
        <div className="ml-5 flex flex-col gap-0.5 border-l border-slate-200 pl-2">
          {group.items.map((item) => (
            <NavLeaf
              key={item.key}
              item={item}
              active={item.key === activeKey}
              onSelect={onSelect}
              id={id}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NavLeaf({
  item,
  active,
  onSelect,
  id,
}: {
  item: ProfileNavItem;
  active: boolean;
  onSelect: (key: string) => void;
  id: string;
}) {
  return (
    <button
      type="button"
      id={`${id}-${item.key}`}
      onClick={() => onSelect(item.key)}
      aria-current={active ? 'page' : undefined}
      className={[
        'flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition',
        active ? 'bg-blue-50 text-slate-900' : 'text-slate-600 hover:bg-slate-50',
      ].join(' ')}
    >
      {item.icon ? (
        <span
          className={`shrink-0 ${active ? 'text-slate-900' : 'text-slate-400'}`}
          aria-hidden="true"
        >
          {item.icon}
        </span>
      ) : null}

      <span className="min-w-0 flex-1 truncate">{item.label}</span>

      {item.count !== undefined ? <CountBadge count={item.count} tone="slate" /> : null}
    </button>
  );
}
